<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { ref } from "vue";
import { Upload, Plus } from "lucide-vue-next";
import { useForm } from "@inertiajs/vue3";
import route from "ziggy-js";
import axios from "axios";
import { useToast } from "@/components/ui/toast/use-toast";
import { useI18n } from "vue-i18n";

const { toast } = useToast();
const { t } = useI18n();

const props = defineProps<{
    imageUrl?: string;
    uploadUrl?: string;
}>();

const emit = defineEmits<{
    "file-uploaded": [file: File];
    "file-uploaded-json": [url: string];
}>();

const photoForm = useForm<{
    image: File | null;
    useWithEventTypes: boolean;
}>({
    image: null,
    useWithEventTypes: true,
});

const HTMLupload = ref<HTMLInputElement>();
const tempFile = ref<string | null>(null);

async function postToServer() {
    // If there is no uploadUrl, we are uploading the image to the profile-photos endpoint
    // This is using an Inertia response
    if (!props.uploadUrl) {
        photoForm.post(route("app.profile-photos.store"), {
            errorBag: "updateProfileInformation",
            preserveScroll: true,
        });

        return;
    }

    // If there is an uploadUrl, we are uploading the image to the provided endpoint
    // This is using an JSON response

    const form = new FormData();
    form.append("image", photoForm.image as File);

    const res = await axios.post(props.uploadUrl, form);

    if (res.data.data.url) {
        emit("file-uploaded-json", res.data.data.url);
    }
}

function onFileAdded() {
    if (!HTMLupload.value || !HTMLupload.value.files) return;

    const file = HTMLupload.value.files[0];

    // Check if file size exceeds 1MB
    if (file && file.size > 1024 * 1024) {
        toast({
            title: t("profile.image_validation.title"),
            description: t("profile.image_validation.description"),
            variant: "destructive",
        });

        HTMLupload.value.value = "";
        return;
    }

    photoForm.image = file;
    postToServer();

    // We will early return becuase using an JSON response we get the URL from the server
    if (props.uploadUrl) return;

    const reader = new FileReader();

    reader.onload = (e) => {
        if (!e.target) return;
        emit("file-uploaded", file);
        tempFile.value = e.target.result as string;
    };

    reader.readAsDataURL(file);
}
</script>
<template>
    <div class="gap-2 flex">
        <div>
            <div class="gap-4 flex">
                <button
                    v-if="imageUrl || tempFile"
                    type="button"
                    class="relative shrink-0"
                    @click.stop="HTMLupload?.click()"
                >
                    <img
                        class="h-20 w-20 max-w-none rounded-full object-cover"
                        :src="tempFile || imageUrl"
                        alt=""
                    />
                </button>

                <button
                    v-else
                    class="group h-20 w-20 rounded-xl border-iron-200 bg-iron-50 ease-out hover:border-iron-300 box-border flex shrink-0 items-center justify-center border-2 transition-all duration-100"
                    @click.stop="HTMLupload?.click()"
                >
                    <div
                        class="h-12 w-12 bg-iron-100 flex items-center justify-center rounded-full"
                    >
                        <Plus
                            class="h-5 w-5 text-z-primary-600 group-hover:h-6 group-hover:w-6"
                        />
                    </div>
                </button>
                <div>
                    <p class="mt-1 text-sm font-medium text-iron-700">
                        {{ $t("onboarding.brand.photo") }}
                    </p>
                    <Button
                        variant="outline"
                        class="mt-2 text-iron-600"
                        type="button"
                        @click.stop="HTMLupload?.click()"
                    >
                        <Upload :size="20" class="mr-2 text-iron-500"></Upload>
                        {{ $t("profile.information.upload_image") }}</Button
                    >
                </div>
            </div>

            <input
                ref="HTMLupload"
                class="hidden"
                type="file"
                @input="onFileAdded"
            />
        </div>
    </div>
</template>
